import { useEffect, useMemo, useState } from 'react'

import { coreApi } from '../../../../api/core'
import { Game } from '../../../../features/game/types/Game'
import { useAppDispatch } from '../../../../hooks/storeHooks'
import { OddCaseHistoryByBlueprint, eventHistoryFromEvolution, oddCaseBlueprintIds, oddCaseGroups } from '../helpers/oddCaseEvents'
import { BlueprintFamilyUsage } from './useBlueprintFamilyUsage'

const requestPoolSize = 6

/**
 * The events on a Blueprint Family whose type it does not accept, grouped by event type.
 *
 * The blueprint carries those events itself, so the groups are there as soon as the page is. How
 * often each event ran is not part of it: that comes from the evolution of the game blueprints the
 * events sit on — only those, usually a handful — and the caller decides when to read it.
 */
export const useBlueprintOddCaseEvents = (usage: BlueprintFamilyUsage, { historyEnabled }: { historyEnabled: boolean }) => {
  const dispatch = useAppDispatch()
  const [histories, setHistories] = useState<OddCaseHistoryByBlueprint>({})
  const [failed, setFailed] = useState(0)

  const groups = useMemo(() => {
    const gameNames: { [gameId: string]: string } = {}
    const gameIcons: { [gameId: string]: ReturnType<Game['getIcon']> } = {}
    const blueprintNames: { [blueprintId: string]: string } = {}
    usage.games.forEach((gameUsage) => {
      const { game } = gameUsage.trackedGame
      gameNames[game.id] = game.name || ''
      gameIcons[game.id] = game.getIcon()
      gameUsage.taggedBlueprints.forEach((blueprint) => {
        blueprintNames[blueprint.blueprintId] = blueprint.names.en || blueprint.blueprintId
      })
    })
    return oddCaseGroups(usage.blueprintFamily.unacceptedEventTypes, { gameNames, gameIcons, blueprintNames })
  }, [usage])

  const blueprintIds = useMemo(() => oddCaseBlueprintIds(groups), [groups])

  useEffect(() => {
    let cancelled = false
    setHistories({})
    setFailed(0)

    if (!historyEnabled || blueprintIds.length === 0) return

    const run = async () => {
      let index = 0
      const worker = async () => {
        while (index < blueprintIds.length) {
          const blueprintId = blueprintIds[index]
          index += 1
          if (cancelled || !blueprintId) return
          try {
            const collection = await dispatch(
              coreApi.endpoints.getTrackedGameLiveEventBlueprints.initiate({ blueprintId: blueprintId, type: 'all' }, { subscribe: false })
            ).unwrap()
            if (!cancelled) setHistories((current) => ({ ...current, [blueprintId]: eventHistoryFromEvolution(collection.evolution) }))
          } catch {
            if (!cancelled) setFailed((count) => count + 1)
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(requestPoolSize, blueprintIds.length) }, () => worker()))
    }

    run()

    return () => {
      cancelled = true
    }
  }, [blueprintIds, dispatch, historyEnabled])

  const checked = Object.keys(histories).length + failed

  return {
    groups,
    histories,
    checked,
    failed,
    total: blueprintIds.length,
    isHistoryComplete: historyEnabled && checked >= blueprintIds.length,
  }
}
